/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import _ from 'lodash';

/**
 * Builds a query parameter from a query object definition
 * @param {Object} parameters - Object containing parameters to be transformed
 *    for a REST request. Current values: full, verbose, filter, search
 * @return {Array} - The array of values to be used as the q query param
 */
export function transformParameters(parameters: any) {
  const result: any = {},
    queries = [];

  if (!parameters) {
    return result;
  }

  if (parameters.include_audit) {
    result.include_audit = true;
  }

  if (parameters.full) {
    result.export = true;
  }

  if (parameters.verbose) {
    result.verbose = true;
  }

  if (parameters.include_count) {
    result.include_count = true;
  }

  if (parameters.page_limit) {
    result.page_limit = parameters.page_limit;
  }

  if (parameters.sort) {
    result.sort = parameters.sort;
  }

  if (parameters.version) {
    result.version = parameters.version;
  }

  if (parameters.filter) {
    queries.push.apply(
      queries,
      _.map(parameters.filter, (value, key) => {
        return key + '~' + value;
      })
    );
  }
  if (parameters.search) {
    queries.push.apply(
      queries,
      _.map(parameters.search, (value, key) => {
        return key + ':' + value;
      })
    );
  }
  if (queries.length) {
    result.q = queries;
  }

  if (parameters.allSystemEntities) {
    result.all_system_entities = true;
  }

  return result;
}

export function serializeParameters(params: Record<string, string>) {
  if (!params) {
    return '';
  }
  const urlParams = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    urlParams.append(name, value || '');
  }
  return urlParams.toString();
}

export const extractErrorMessage = (error: any) => {
  let errorObj = error;

  if (error.response) {
    errorObj = error.response;
    if (_.get(error, 'response.headers.content-type', '') !== 'text/html') {
      errorObj = _.get(error, 'response.data', error.response || error.data);
      // in some cases content is actually under error key, check
      if (errorObj.error && typeof errorObj.error === 'object') {
        // This is actually our error
        errorObj = errorObj.error;
      } else if (errorObj.errors && Array.isArray(errorObj.errors)) {
        // Edge case: Check to see if error is a new integration attempted to be created without a name
        const noNameMessage = 'should NOT be shorter than 1 characters';
        const noNamePath = '/name';
        const firstErrorMessage = errorObj.errors[0].message;
        const firstErrorPath = errorObj.errors[0].path;
        if (
          firstErrorMessage === noNameMessage &&
          firstErrorPath === noNamePath
        ) {
          errorObj = errorObj.errors[0];
          errorObj.message =
            'Integration name should NOT be shorter than 1 character.';
        } else {
          errorObj = errorObj.errors.map(e => e?.message || e).join(', ');
          return { error: errorObj };
        }
      }
    }
    if (typeof errorObj !== 'object') {
      errorObj = error.response;
    }
  }
  let errorMessage =
    errorObj.message ||
    errorObj.error ||
    errorObj.statusText ||
    (error.toString && error.toString()) ||
    error;

  // NOTE:  In the past, we would not return an object, just empty string, now,
  // error.message can be an intl message object (see react-intl) and it can
  // be displayed by Notification.
  // If message has 'id' property we'll leave it alone
  if (typeof errorMessage !== 'string' && !errorMessage?.id) {
    errorMessage = '';
  }

  return errorMessage;
};

type IAMErrorData = {
  authData?: {
    isSessionRevoked?: boolean;
    isSessionInactive?: boolean;
    ipAddressRestriction?: boolean;
  };
};
export const isAuthSessionRevoked = (error: AxiosError<IAMErrorData>) => {
  const status = error.response?.status;
  const isSessionRevoked = error.response?.data?.authData?.isSessionRevoked;

  return status === StatusCodes.GONE && isSessionRevoked;
};

export const isAuthSessionInactive = (error: AxiosError<IAMErrorData>) => {
  const status = error.response?.status;
  const isSessionInactive = error.response?.data?.authData?.isSessionInactive;

  return status === StatusCodes.GONE && isSessionInactive;
};

export const isAuthSessionIpAddressRestriction = (
  error: AxiosError<IAMErrorData>
) => {
  const status = error.response?.status;
  const isSessionIpAddressRestriction =
    error.response?.data?.authData?.ipAddressRestriction;

  return status === StatusCodes.GONE && isSessionIpAddressRestriction;
};
